import { 
  collection, 
  collectionGroup,
  doc, 
  getDocs, 
  getDoc,
  onSnapshot,
  addDoc, 
  setDoc,
  updateDoc, 
  deleteDoc,
  query,
  where,
  orderBy,
  Timestamp,
  writeBatch
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { normalizeThemePreferences } from '../utils/themePreferences';

// Export db and doc for external use
export { db, doc };

// Lightweight in-memory cache to reduce repeated reads during a single session.
// This avoids changing function signatures across the app and gives an easy
// win for pages that fetch the same collections repeatedly.
const _cache = new Map();

const defaultTTL = 1000 * 60 * 5; // 5 minutes

const _makeKey = (name, args) => `${name}:${JSON.stringify(args ?? {})}`;

const _getCached = (key) => {
  const entry = _cache.get(key);
  if (!entry) return null;
  if (entry.expire && Date.now() > entry.expire) {
    _cache.delete(key);
    return null;
  }
  return entry.value;
};

const _setCached = (key, value, ttl = defaultTTL) => {
  _cache.set(key, { value, expire: Date.now() + ttl });
};

export const clearCache = (key) => {
  if (!key) return _cache.clear();
  _cache.delete(key);
};

export const clearCachePrefix = (prefix) => {
  for (const key of Array.from(_cache.keys())) {
    if (key.startsWith(prefix)) _cache.delete(key);
  }
};

// Helper to convert lesson IDs to Firestore document references
// This function validates that lessons exist before creating references
export const convertLessonIdsToRefs = async (planData) => {
  if (!planData || typeof planData !== 'object') return planData;
  
  const converted = {};
  
  // First, check what lessons exist in the sessions collection
  const sessionsSnapshot = await getDocs(collection(db, 'sessions'));
  const sessionIds = new Set(sessionsSnapshot.docs.map(d => d.id));
  
  // Get all curriculum grades to search for lessons
  const gradesSnapshot = await getDocs(collection(db, 'curriculum_index')).catch(() => null);
  const gradesList = gradesSnapshot?.docs?.[0]?.data()?.grades || [];
  
  // Build a map of lesson ID -> document path
  const lessonPaths = new Map();
  
  // Add all sessions
  sessionIds.forEach(sid => {
    lessonPaths.set(sid, ['sessions', sid]);
  });
  
  // Search curriculum for lessons
  for (const grade of gradesList) {
    try {
      const gradeCollectionRef = collection(db, grade);
      const subjectsSnapshot = await getDocs(gradeCollectionRef);
      
      for (const subjectDoc of subjectsSnapshot.docs) {
        const lessonsRef = collection(db, grade, subjectDoc.id, 'lessons');
        const lessonsSnapshot = await getDocs(lessonsRef);
        
        for (const lessonDoc of lessonsSnapshot.docs) {
          lessonPaths.set(lessonDoc.id, [grade, subjectDoc.id, 'lessons', lessonDoc.id]);
        }
      }
    } catch (err) {
      console.warn(`Could not load lessons from ${grade}:`, err);
    }
  }
  
  // Now convert the plan data
  for (const [studentId, plan] of Object.entries(planData)) {
    const studentPlan = {};
    
    for (const [category, sessions] of Object.entries(plan)) {
      if (category === 'saved' || category === 'techniques') {
        studentPlan[category] = sessions;
        continue;
      }
      
      if (Array.isArray(sessions)) {
        studentPlan[category] = sessions
          .map(sessionId => {
            // Check if it's already a reference
            if (sessionId && typeof sessionId === 'object' && sessionId.path) {
              return sessionId;
            }
            
            // Convert string ID to reference if lesson exists
            if (typeof sessionId === 'string') {
              const path = lessonPaths.get(sessionId);
              if (path) {
                return doc(db, ...path);
              }
              // Keep unknown string IDs (for settings-driven activity IDs like body__/mind__).
              return sessionId;
            }
            
            return sessionId;
          })
          .filter(ref => ref !== null); // Remove null entries
      } else {
        studentPlan[category] = sessions;
      }
    }
    
    converted[studentId] = studentPlan;
  }
  
  return converted;
};

// Helper to convert references back to IDs for reading
export const convertRefsToLessonIds = (planData) => {
  if (!planData || typeof planData !== 'object') return planData;
  
  const converted = {};
  
  for (const [studentId, plan] of Object.entries(planData)) {
    const studentPlan = {};
    
    for (const [category, sessions] of Object.entries(plan)) {
      if (category === 'saved' || category === 'techniques') {
        studentPlan[category] = sessions;
        continue;
      }
      
      if (Array.isArray(sessions)) {
        studentPlan[category] = sessions.map(session => {
          // If it's a reference object with path, extract the ID
          if (session && typeof session === 'object' && session.path) {
            const parts = session.path.split('/');
            return parts[parts.length - 1];
          }
          // If it's already a string ID, return as is
          if (typeof session === 'string') {
            return session;
          }
          return session;
        });
      } else {
        studentPlan[category] = sessions;
      }
    }
    
    converted[studentId] = studentPlan;
  }
  
  return converted;
};

// Helper to build daily report data with the required schema
export function buildDailyReportData({
  date,
  physicalFitness = { activities: {} },
  mind = { activities: {} },
  intellect = { activities: {}, skills: {} },
  therapeuticInterventions = { activities: {} },
  demeanourObservations = {
    observationType: [],
    sensoryRegulation: '',
    sensoryRegulationNotes: '',
    emotionalRegulation: '',
    emotionalRegulationNotes: '',
    behavioursNotes: ''
  }
}) {
  const normalizeActivity = (activity = {}) => ({
    subject: activity.subject || '',
    topic: activity.topic || '',
    sessionType: activity.sessionType || '',
    engagementText: activity.engagementText || '',
    engagementLevel: activity.engagementLevel ?? '',
    techniqueUsed: activity.techniqueUsed || '',
    techniqueCustom: activity.techniqueCustom || '',
    involvement: activity.involvement || '',
    notes: activity.notes || '',
    domain: activity.domain || '',
    observableOutcomes: activity.observableOutcomes || '',
    redFlags: activity.redFlags || '',
    actualParticipationLevel: activity.actualParticipationLevel || ''
  });

  const normalizeActivitiesMap = (activities = {}) => {
    if (!activities || typeof activities !== 'object') return {};
    return Object.entries(activities).reduce((next, [key, value]) => {
      if (!key) return next;
      next[key] = normalizeActivity(value);
      return next;
    }, {});
  };

  return {
    date,
    physicalFitness: {
      activities: normalizeActivitiesMap(physicalFitness.activities)
    },
    mind: {
      activities: normalizeActivitiesMap(mind.activities)
    },
    intellect: {
      activities: normalizeActivitiesMap(intellect.activities),
      skills: normalizeActivitiesMap(intellect.skills)
    },
    therapeuticInterventions: {
      activities: normalizeActivitiesMap(therapeuticInterventions.activities)
    },
    demeanourObservations: {
      observationType: Array.isArray(demeanourObservations.observationType) ? demeanourObservations.observationType : [],
      sensoryRegulation: demeanourObservations.sensoryRegulation || '',
      sensoryRegulationNotes: demeanourObservations.sensoryRegulationNotes || '',
      emotionalRegulation: demeanourObservations.emotionalRegulation || '',
      emotionalRegulationNotes: demeanourObservations.emotionalRegulationNotes || '',
      behavioursNotes: demeanourObservations.behavioursNotes || ''
    }
  };
}

// Collections
const COLLECTIONS = {
  STUDENTS: 'students',
  MENTORS: 'mentors',
  DAILY_REPORTS: 'dailyReports',
  WEEKLY_PLANS: 'weeklyPlans',
  TIMETABLE: 'timetable',
  SESSIONS: 'sessions',
  IMPORTANT_DATES: 'importantDates',
  GALLERY: 'gallery',
  BROADCASTS: 'broadcasts',
  SETTINGS: 'settings'
  ,ACADEMIC_PROGRESS: 'academicProgress'
  ,WPM_REPORTS: 'wpmReports'
  ,STUDENT_PROGRESS_HISTORY: 'studentProgressHistory'
  ,WPM_MILESTONE_CONFIG: 'wpmMilestoneConfig',
  EXTERNAL_ASSESSMENTS: 'externalAssessments',
  STUDENT_INTELLECT_PROGRESS: 'studentIntellectProgress',
  STUDENT_BASELINE_OBSERVATIONS: 'studentBaselineObservations'
};

// ============ STUDENTS ============
const _normalizeStudent = (raw) => {
  if (!raw) return null;
  const s = { ...raw };
  // Ensure id exists
  if (!s.id && typeof s._id !== 'undefined') s.id = s._id;

  // Basic defaults
  s.name = s.name || s.fullName || s.displayName || 'Unnamed Student';

  // Normalize grades object: coerce numeric values to 'Grade N' strings
  const normalizeGradeValue = (val) => {
    if (typeof val === 'number') return `Grade ${val}`;
    if (typeof val === 'string' && /^\d+$/.test(val)) return `Grade ${val}`;
    return String(val);
  };

  if (s.grades && typeof s.grades === 'object') {
    const normalized = {};
    for (const [k, v] of Object.entries(s.grades || {})) {
      normalized[k] = v === undefined || v === null ? v : normalizeGradeValue(v);
    }
    s.grades = normalized;
  } else {
    s.grades = {};
  }

  // If a top-level numeric 'grade' is present, coerce to 'Grade N'
  if (s.grade !== undefined && s.grade !== null) {
    if (typeof s.grade === 'number') s.grade = `Grade ${s.grade}`;
    else if (typeof s.grade === 'string' && /^\d+$/.test(s.grade)) s.grade = `Grade ${s.grade}`;
  }

  // If no explicit grade provided, try to infer from normalized grades map
  if (!s.grade) {
    const first = Object.values(s.grades)[0];
    s.grade = first || '';
  }

  s.selectedSubjects = Array.isArray(s.selectedSubjects) ? s.selectedSubjects : (s.selectedSubjects ? [s.selectedSubjects] : []);
  s.demographics = s.demographics || {};
  s.abc = s.abc || {};
  s.functionalAge = s.functionalAge ?? null;

  return s;
};

export const getStudents = async (opts = {}) => {
  // opts may include { forceRefresh: true, limit }
  const cacheKey = _makeKey('students', opts);
  if (!opts.forceRefresh) {
    const cached = _getCached(cacheKey);
    if (cached) return cached;
  }

  // Optionally support a limit in the future; currently apps call without opts
  const querySnapshot = await getDocs(collection(db, COLLECTIONS.STUDENTS));
  const result = querySnapshot.docs.map(doc => _normalizeStudent({ id: doc.id, ...doc.data() }));
  _setCached(cacheKey, result);
  return result;
};

export const getStudent = async (studentId) => {
  const docRef = doc(db, COLLECTIONS.STUDENTS, studentId);
  const docSnap = await getDoc(docRef);
  return docSnap.exists() ? _normalizeStudent({ id: docSnap.id, ...docSnap.data() }) : null;
};

export const addStudent = async (studentData) => {
  const prepared = {
    ...studentData,
    grades: studentData.grades || {},
    selectedSubjects: Array.isArray(studentData.selectedSubjects) ? studentData.selectedSubjects : (studentData.selectedSubjects ? [studentData.selectedSubjects] : []),
    createdAt: Timestamp.now(),
    updatedAt: Timestamp.now()
  };
  const docRef = await addDoc(collection(db, COLLECTIONS.STUDENTS), prepared);
  // Invalidate students cache so subsequent reads return fresh data
  clearCachePrefix('students');
  return docRef.id;
};

// invalidate cached student lists
addStudent.clearCacheOnWrite = () => clearCachePrefix('students');

export const updateStudent = async (studentId, studentData) => {
  const prepared = {
    ...studentData,
    grades: studentData.grades || {},
    selectedSubjects: Array.isArray(studentData.selectedSubjects) ? studentData.selectedSubjects : (studentData.selectedSubjects ? [studentData.selectedSubjects] : []),
    updatedAt: Timestamp.now()
  };
  const docRef = doc(db, COLLECTIONS.STUDENTS, studentId);
  await updateDoc(docRef, prepared);
  // Invalidate students cache
  clearCachePrefix('students');
};

updateStudent.clearCacheOnWrite = () => clearCachePrefix('students');

export const deleteStudent = async (studentId) => {
  await deleteDoc(doc(db, COLLECTIONS.STUDENTS, studentId));
  // Clear cached students list after delete
  clearCachePrefix('students');
};

deleteStudent.clearCacheOnWrite = () => clearCachePrefix('students');

// ============ MENTORS ============
export const getMentors = async (opts = {}) => {
  const cacheKey = _makeKey('mentors', opts);
  if (!opts.forceRefresh) {
    const cached = _getCached(cacheKey);
    if (cached) return cached;
  }

  const querySnapshot = await getDocs(collection(db, COLLECTIONS.MENTORS));
  const result = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  _setCached(cacheKey, result);
  return result;
};

export const getMentor = async (mentorId) => {
  const docRef = doc(db, COLLECTIONS.MENTORS, mentorId);
  const docSnap = await getDoc(docRef);
  return docSnap.exists() ? { id: docSnap.id, ...docSnap.data() } : null;
};

export const addMentor = async (mentorData) => {
  const docRef = await addDoc(collection(db, COLLECTIONS.MENTORS), {
    ...mentorData,
    createdAt: Timestamp.now(),
    updatedAt: Timestamp.now()
  });
  clearCachePrefix('mentors');
  return docRef.id;
};

addMentor.clearCacheOnWrite = () => clearCachePrefix('mentors');

export const updateMentor = async (mentorId, mentorData) => {
  const docRef = doc(db, COLLECTIONS.MENTORS, mentorId);
  await updateDoc(docRef, {
    ...mentorData,
    updatedAt: Timestamp.now()
  });
  clearCachePrefix('mentors');
};

updateMentor.clearCacheOnWrite = () => clearCachePrefix('mentors');

export const deleteMentor = async (mentorId) => {
  await deleteDoc(doc(db, COLLECTIONS.MENTORS, mentorId));
  clearCachePrefix('mentors');
};

deleteMentor.clearCacheOnWrite = () => clearCachePrefix('mentors');

// ============ USERS (Auth -> Firestore mapping) ============
export const getUsers = async (opts = {}) => {
  const cacheKey = _makeKey('users', opts);
  if (!opts.forceRefresh) {
    const cached = _getCached(cacheKey);
    if (cached) return cached;
  }

  const querySnapshot = await getDocs(collection(db, 'users'));
  const result = querySnapshot.docs.map(d => ({ id: d.id, ...d.data() }));
  _setCached(cacheKey, result);
  return result;
};

export const getUser = async (userId) => {
  const docRef = doc(db, 'users', userId);
  const snap = await getDoc(docRef);
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
};

export const upsertUser = async (userId, userData) => {
  const docRef = doc(db, 'users', userId);
  await setDoc(docRef, { ...userData, id: userId, updatedAt: Timestamp.now() }, { merge: true });
  clearCachePrefix('users');
  return userId;
};

export const addUser = async (userData) => {
  const docRef = await addDoc(collection(db, 'users'), { ...userData, createdAt: Timestamp.now(), updatedAt: Timestamp.now() });
  clearCachePrefix('users');
  return docRef.id;
};

export const deleteUser = async (userId) => {
  await deleteDoc(doc(db, 'users', userId));
  clearCachePrefix('users');
};

deleteUser.clearCacheOnWrite = () => clearCachePrefix('users');

// ============ DAILY REPORTS ============
export const normalizeDailyReport = (raw) => {
  if (!raw || typeof raw !== 'object') return raw;

  const normalizeMap = (map = {}) => {
    if (!map || typeof map !== 'object') return {};
    return Object.entries(map).reduce((result, [key, value]) => {
      if (!key) return result;
      const item = value || {};
      result[key] = {
        subject: item.subject || key,
        topic: item.topic || '',
        sessionType: item.sessionType || '',
        engagementText: item.engagementText || '',
        engagementLevel: item.engagementLevel ?? '',
        techniqueUsed: item.techniqueUsed || '',
        techniqueCustom: item.techniqueCustom || '',
        involvement: item.involvement || '',
        notes: item.notes || '',
        domain: item.domain || '',
        observableOutcomes: item.observableOutcomes || '',
        redFlags: item.redFlags || ''
      };
      return result;
    }, {});
  };

  const normalizeIntellectSkills = (skills = {}) => {
    if (!skills || typeof skills !== 'object') return {};
    return Object.entries(skills).reduce((result, [key, value]) => {
      if (!key) return result;
      const item = value || {};
      result[key] = {
        subject: item.subject || key,
        topic: item.topic || '',
        engagementText: item.engagementText || '',
        engagementLevel: item.engagementLevel ?? '',
        notes: item.notes || '',
        lessonsDone: item.lessonsDone || ''
      };
      return result;
    }, {});
  };

  const demeanourSource = raw.demeanourObservations || raw.demeanour || raw.demeanor || {};

  const normalized = {
    ...raw,
    physicalFitness: {
      activities: normalizeMap(raw.physicalFitness?.activities)
    },
    mind: {
      activities: normalizeMap(raw.mind?.activities)
    },
    intellect: {
      activities: normalizeMap(raw.intellect?.activities),
      skills: normalizeIntellectSkills(raw.intellect?.skills)
    },
    demeanourObservations: {
      observationType: Array.isArray(demeanourSource?.observationType) ? demeanourSource.observationType : [],
      sensoryRegulation: demeanourSource?.sensoryRegulation || '',
      sensoryRegulationNotes: demeanourSource?.sensoryRegulationNotes || '',
      emotionalRegulation: demeanourSource?.emotionalRegulation || '',
      emotionalRegulationNotes: demeanourSource?.emotionalRegulationNotes || '',
      behavioursNotes: demeanourSource?.behavioursNotes || ''
    }
  };

  if (!Object.keys(normalized.intellect.activities).length) {
    ['english', 'math', 'evs'].forEach((subjectKey) => {
      const subj = raw.intellect?.[subjectKey];
      if (subj && typeof subj === 'object') {
        const normalizedKey = subjectKey;
        normalized.intellect.activities[normalizedKey] = {
          subject: subjectKey,
          topic: subj.lessonsDone || subj.topic || '',
          sessionType: subj.sessionType || '',
          engagementText: subj.engagementText || '',
          engagementLevel: subj.engagementLevel ?? '',
          techniqueUsed: subj.techniqueUsed || '',
          techniqueCustom: subj.techniqueCustom || '',
          involvement: subj.involvement || '',
          notes: subj.notes || '',
          domain: subj.domain || '',
          observableOutcomes: subj.observableOutcomes || '',
          redFlags: subj.redFlags || ''
        };
      }
    });
  }

  if (!Object.keys(normalized.intellect.skills).length && raw.intellect?.skills && typeof raw.intellect.skills === 'object') {
    normalized.intellect.skills = normalizeIntellectSkills(raw.intellect.skills);
  }

  return normalized;
};

export const getDailyReports = async (filters = {}) => {
  const cacheKey = _makeKey('dailyReports', filters);
  if (!filters.forceRefresh) {
    const cached = _getCached(cacheKey);
    if (cached) return cached;
  }
  let q = collection(db, COLLECTIONS.DAILY_REPORTS);
  
  if (filters.studentId) {
    q = query(q, where('studentId', '==', filters.studentId));
  }
  if (filters.date) {
    q = query(q, where('date', '==', filters.date));
  }
  
  const querySnapshot = await getDocs(q);
  const result = querySnapshot.docs.map(doc => {
    const data = normalizeDailyReport(doc.data());
    return {
      ...data,
      id: data?.id || doc.id,
      docId: doc.id
    };
  });
  _setCached(cacheKey, result);
  return result;
};

export const getDailyReport = async (reportId) => {
  const docRef = doc(db, COLLECTIONS.DAILY_REPORTS, reportId);
  const docSnap = await getDoc(docRef);
  if (!docSnap.exists()) return null;
  const data = normalizeDailyReport(docSnap.data());
  return { ...data, id: data?.id || docSnap.id, docId: docSnap.id };
};

export const getDailyReportByIdentity = async ({ studentId, mentorId, date, forceRefresh = false } = {}) => {
  if (!studentId || !mentorId || !date) return null;

  const filters = { studentId, mentorId, date };
  const cacheKey = _makeKey('dailyReportByIdentity', filters);
  if (!forceRefresh) {
    const cached = _getCached(cacheKey);
    if (cached) return cached;
  }

  const q = query(
    collection(db, COLLECTIONS.DAILY_REPORTS),
    where('studentId', '==', studentId),
    where('mentorId', '==', mentorId),
    where('date', '==', date)
  );

  const querySnapshot = await getDocs(q);
  if (!querySnapshot.docs.length) {
    _setCached(cacheKey, null);
    return null;
  }

  const docs = querySnapshot.docs.map((entry) => {
    const data = normalizeDailyReport(entry.data());
    return {
      ...data,
      id: data?.id || entry.id,
      docId: entry.id
    };
  });

  const toMillis = (value) => {
    if (!value) return 0;
    if (typeof value?.toMillis === 'function') return value.toMillis();
    const parsed = new Date(value).getTime();
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const latest = docs.sort((a, b) => {
    const aUpdated = toMillis(a.updatedAt) || toMillis(a.createdAt);
    const bUpdated = toMillis(b.updatedAt) || toMillis(b.createdAt);
    return bUpdated - aUpdated;
  })[0] || null;

  _setCached(cacheKey, latest);
  return latest;
};

// Usage: Pass the object returned by buildDailyReportData to this function
export const addDailyReport = async (reportData) => {
  const docRef = await addDoc(collection(db, COLLECTIONS.DAILY_REPORTS), {
    ...reportData,
    createdAt: Timestamp.now(),
    updatedAt: Timestamp.now()
  });
  clearCachePrefix('dailyReports');
  clearCachePrefix('dailyReportByIdentity');
  return docRef.id;
};

addDailyReport.clearCacheOnWrite = () => clearCachePrefix('dailyReports');

export const updateDailyReport = async (reportId, reportData) => {
  const docRef = doc(db, COLLECTIONS.DAILY_REPORTS, reportId);
  await updateDoc(docRef, {
    ...reportData,
    updatedAt: Timestamp.now()
  });
  clearCachePrefix('dailyReports');
  clearCachePrefix('dailyReportByIdentity');
};

updateDailyReport.clearCacheOnWrite = () => clearCachePrefix('dailyReports');

export const deleteDailyReport = async (reportId) => {
  await deleteDoc(doc(db, COLLECTIONS.DAILY_REPORTS, reportId));
  clearCachePrefix('dailyReports');
  clearCachePrefix('dailyReportByIdentity');
};

// Generic helper to set a document with a chosen id (useful for seed scripts)
export const setDocument = async (collectionName, id, data) => {
  const docRef = doc(db, collectionName, id);
  await setDoc(docRef, {
    ...data,
    createdAt: data.createdAt ?? Timestamp.now(),
    updatedAt: Timestamp.now()
  });
  clearCachePrefix(collectionName);
  return id;
};

// Generic helper to delete a document by id
export const deleteDocument = async (collectionName, id) => {
  await deleteDoc(doc(db, collectionName, id));
  clearCachePrefix(collectionName);
};

// Fetch all curriculum lesson documents
export const getCurriculum = async (opts = {}) => {
  const cacheKey = _makeKey('curriculum', opts);
  if (!opts.forceRefresh) {
    const cached = _getCached(cacheKey);
    if (cached) return cached;
  }

  const querySnapshot = await getDocs(collection(db, 'curriculum'));
  const result = querySnapshot.docs.map(d => ({ id: d.id, docId: d.id, ...d.data() }));
  _setCached(cacheKey, result);
  return result;
};

// Generic helpers for arbitrary collections and sub-documents
export const getDocument = async (collectionName, id) => {
  const docRef = doc(db, collectionName, id);
  const snap = await getDoc(docRef);
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
};

export const listCollectionDocs = async (collectionName, opts = {}) => {
  const cacheKey = _makeKey(`collection:${collectionName}`, opts);
  if (!opts.forceRefresh) {
    const cached = _getCached(cacheKey);
    if (cached) return cached;
  }
  const querySnapshot = await getDocs(collection(db, collectionName));
  const result = querySnapshot.docs.map(d => ({ id: d.id, docId: d.id, ...d.data() }));
  _setCached(cacheKey, result);
  return result;
};

export const getSubDocument = async (collectionName, docId, subCollectionName, subDocId) => {
  const docRef = doc(db, collectionName, docId, subCollectionName, subDocId);
  const snap = await getDoc(docRef);
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
};

export const listSubCollectionDocs = async (collectionName, docId, subCollectionName, opts = {}) => {
  const cacheKey = _makeKey(`${collectionName}:${docId}:${subCollectionName}`, opts);
  if (!opts.forceRefresh) {
    const cached = _getCached(cacheKey);
    if (cached) return cached;
  }
  const querySnapshot = await getDocs(collection(db, collectionName, docId, subCollectionName));
  const result = querySnapshot.docs.map(d => ({ id: d.id, docId: d.id, ...d.data() }));
  _setCached(cacheKey, result);
  return result;
};

export const setSubDocument = async (collectionName, docId, subCollectionName, subDocId, data) => {
  const docRef = doc(db, collectionName, docId, subCollectionName, subDocId);
  await setDoc(docRef, {
    ...data,
    createdAt: data.createdAt ?? Timestamp.now(),
    updatedAt: Timestamp.now()
  });
  // Invalidate cache for that collection and the precise path
  clearCachePrefix(`collection:${collectionName}`);
  clearCachePrefix(`${collectionName}:${docId}:${subCollectionName}`);
  return subDocId;
};

export const deleteSubDocument = async (collectionName, docId, subCollectionName, subDocId) => {
  const docRef = doc(db, collectionName, docId, subCollectionName, subDocId);
  await deleteDoc(docRef);
  clearCachePrefix(`${collectionName}:${docId}:${subCollectionName}`);
};

export const deleteNestedSubDocument = async (collectionName, docId, subCollectionName, subDocId, nestedSubCollectionName, nestedSubDocId) => {
  const docRef = doc(db, collectionName, docId, subCollectionName, subDocId, nestedSubCollectionName, nestedSubDocId);
  await deleteDoc(docRef);
  clearCachePrefix(`${collectionName}:${docId}:${subCollectionName}:${subDocId}:${nestedSubCollectionName}`);
};

// New: helpers for nested subcollections (e.g., timetable/<day>/slots/<slot>/assignments/<assignment>)
export const setNestedSubDocument = async (collectionName, docId, subCollectionName, subDocId, nestedSubCollectionName, nestedSubDocId, data) => {
  const docRef = doc(db, collectionName, docId, subCollectionName, subDocId, nestedSubCollectionName, nestedSubDocId);
  await setDoc(docRef, {
    ...data,
    createdAt: data.createdAt ?? Timestamp.now(),
    updatedAt: Timestamp.now()
  });
  // Invalidate relevant caches
  clearCachePrefix(`collection:${collectionName}`);
  clearCachePrefix(`${collectionName}:${docId}:${subCollectionName}`);
  clearCachePrefix(`${collectionName}:${docId}:${subCollectionName}:${subDocId}:${nestedSubCollectionName}`);
  return nestedSubDocId;
};

export const listNestedSubCollectionDocs = async (collectionName, docId, subCollectionName, subDocId, nestedSubCollectionName, opts = {}) => {
  const cacheKey = _makeKey(`${collectionName}:${docId}:${subCollectionName}:${subDocId}:${nestedSubCollectionName}`, opts);
  if (!opts.forceRefresh) {
    const cached = _getCached(cacheKey);
    if (cached) return cached;
  }
  const querySnapshot = await getDocs(collection(db, collectionName, docId, subCollectionName, subDocId, nestedSubCollectionName));
  const result = querySnapshot.docs.map(d => ({ id: d.id, docId: d.id, ...d.data() }));
  _setCached(cacheKey, result);
  return result;
};

// Convenience helper: set a slot assignment as a subdocument; the data can include ids and will be normalized to store references
export const setSlotAssignment = async (dayDocId, slotId, assignmentId, { mentorId, studentIds = [], sessionType = '', subject = '' }) => {
  // Ensure the day doc exists
  const dayDocRef = doc(db, COLLECTIONS.TIMETABLE, dayDocId);
  await setDoc(dayDocRef, { day: dayDocId, updatedAt: Timestamp.now(), canonical: true }, { merge: true });

  // Ensure slot doc exists under day
  await setSubDocument(COLLECTIONS.TIMETABLE, dayDocId, 'slots', slotId, { slot: slotId, updatedAt: Timestamp.now() });

  // Normalize to store DocumentReference objects for mentor and students
  const assignmentData = {
    mentorRef: mentorId ? doc(db, COLLECTIONS.MENTORS, mentorId) : null,
    studentRefs: (studentIds || []).map(sid => doc(db, COLLECTIONS.STUDENTS, sid)),
    sessionType,
    subject
  };

  await setNestedSubDocument(COLLECTIONS.TIMETABLE, dayDocId, 'slots', slotId, 'assignments', assignmentId, assignmentData);
  return assignmentId;
};

export const listSlotAssignments = async (dayDocId, slotId, opts = {}) => {
  const docs = await listNestedSubCollectionDocs(COLLECTIONS.TIMETABLE, dayDocId, 'slots', slotId, 'assignments', opts).catch(() => []);
  // Normalize references to ids for client convenience. Support multiple storage styles:
  // - mentorRef: DocumentReference object
  // - mentorRef: string id
  // - mentorId: explicit field
  // For studentRefs elements, support DocumentReference objects or string ids.
  const extractId = (ref) => {
    if (!ref && ref !== 0) return null;
    if (typeof ref === 'string') return ref;
    if (ref && typeof ref === 'object') {
      if (ref.id) return ref.id;
      if (ref.path && typeof ref.path === 'string') {
        // path like 'collection/docId' or longer - take last segment
        const parts = ref.path.split('/');
        return parts[parts.length - 1] || null;
      }
    }
    return null;
  };

  return docs.map(d => ({
    id: d.id,
    mentorId: extractId(d.mentorRef) || d.mentorId || null,
    studentIds: Array.isArray(d.studentRefs) ? d.studentRefs.map(r => extractId(r)).filter(Boolean) : (Array.isArray(d.studentIds) ? d.studentIds : (d.studentIds ? [d.studentIds] : [])),
    sessionType: d.sessionType || '',
    subject: d.subject || '',
    raw: d
  }));
};

// Count assignments for a student optionally filtered by subject using a collectionGroup query
export const countAssignmentsForStudentBySubject = async (studentId, subject = null) => {
  try {
    // 1) Prefer DocumentReference-based studentRefs
    const studentRef = doc(db, COLLECTIONS.STUDENTS, studentId);
    let constraints = [where('studentRefs', 'array-contains', studentRef)];
    if (subject) constraints.push(where('subject', '==', subject));
    let q = query(collectionGroup(db, 'assignments'), ...constraints);
    let snap = await getDocs(q);
    if (snap && snap.size > 0) { console.debug('countAssignmentsForStudentBySubject: matched using studentRefs DocumentReference', { studentId, subject, count: snap.size }); return snap.size; }

    // 2) Fallback: some assignments may store student IDs in 'studentIds'
    let legacyConstraints = [where('studentIds', 'array-contains', studentId)];
    if (subject) legacyConstraints.push(where('subject', '==', subject));
    let q2 = query(collectionGroup(db, 'assignments'), ...legacyConstraints);
    let snap2 = await getDocs(q2);
    if (snap2 && snap2.size > 0) { console.debug('countAssignmentsForStudentBySubject: matched using studentIds array-contains', { studentId, subject, count: snap2.size }); return snap2.size; }

    // 3) Another fallback: some assignments may store studentRefs as plain string IDs in 'studentRefs'
    try {
      let stringConstraints = [where('studentRefs', 'array-contains', studentId)];
      if (subject) stringConstraints.push(where('subject', '==', subject));
      let q3 = query(collectionGroup(db, 'assignments'), ...stringConstraints);
      let snap3 = await getDocs(q3);
      if (snap3 && snap3.size > 0) { console.debug('countAssignmentsForStudentBySubject: matched using studentRefs string IDs', { studentId, subject, count: snap3.size }); return snap3.size; }
    } catch (err) {
      console.warn('countAssignmentsForStudentBySubject: collectionGroup string-ID query failed', err);
    }

    // 4) Final fallback: do a client-side scan of timetable slots/assignments as a reliable fallback
    try {
      let scanCount = 0;
      const timetableDocs = await getDocs(collection(db, COLLECTIONS.TIMETABLE));
      for (const d of timetableDocs.docs) {
        const slots = await listSubCollectionDocs(COLLECTIONS.TIMETABLE, d.id, 'slots').catch(() => []);
        for (const s of slots) {
          const assigns = await listNestedSubCollectionDocs(COLLECTIONS.TIMETABLE, d.id, 'slots', s.id, 'assignments').catch(() => []);
          for (const a of assigns) {
            // check subject if requested
            if (subject && String((a.subject || '')).toLowerCase() !== String(subject).toLowerCase()) continue;
            const studentRefsArr = Array.isArray(a.studentRefs) ? a.studentRefs.map(r => (r && r.id) ? r.id : (typeof r === 'string' ? r : null)).filter(Boolean) : [];
            const studentIdsArr = Array.isArray(a.studentIds) ? a.studentIds : (a.studentIds ? [a.studentIds] : []);
            if (studentRefsArr.includes(studentId) || studentIdsArr.includes(studentId)) scanCount++;
          }
        }
      }
      console.debug('countAssignmentsForStudentBySubject: scanned timetable, found', { studentId, subject, count: scanCount });
      return scanCount;
    } catch (err) {
      console.warn('countAssignmentsForStudentBySubject: scan fallback failed', err);
    }

    return 0;
  } catch (err) {
    console.warn('countAssignmentsForStudentBySubject failed', err);
    return 0;
  }
};

// Curriculum index helpers (list of seeded grade collections)
export const getCurriculumGrades = async () => {
  // First try index doc
  const idx = await getDocument('curriculum_index', 'grades');
  if (idx && Array.isArray(idx.grades) && idx.grades.length > 0) return idx.grades;

  // Fallback: detect legacy 'curriculum' collection and return it as a single grade
  try {
    const legacy = await listCollectionDocs('curriculum');
    if (legacy && legacy.length > 0) return ['curriculum'];
  } catch (err) {
    // ignore
  }

  return [];
};

export const getAllCurriculumLessons = async () => {
  const grades = await getCurriculumGrades();
  const lessons = [];
  const allCollections = Array.from(new Set([...(grades || []), 'Body Activities']));

  // If no grades found, attempt a last-ditch read of 'curriculum' (legacy)
  if ((!grades || grades.length === 0)) {
    const legacyDocs = await listCollectionDocs('curriculum', { forceRefresh: true }).catch(() => []);
    for (const d of legacyDocs) {
      const meta = await getSubDocument('curriculum', d.id, 'content', 'meta').catch(() => null);
      lessons.push({ id: d.id, grade: 'curriculum', subject: d.subject || meta?.subject || '', topic: d.topic || meta?.topic || d.id, ...(meta || {}) });
    }
    return lessons;
  }

  for (const gradeName of allCollections) {
    const docs = await listCollectionDocs(gradeName, { forceRefresh: true }).catch(() => []);
    for (const d of docs) {
      // If this doc is a subject container, list its 'lessons' subcollection
      if (d.__isSubject || String(d.id).startsWith('subject__')) {
        const subjectName = d.subject || d.id.replace(/^subject__/, '').split('__')[0];
        const lessonsDocs = await listSubCollectionDocs(gradeName, d.id, 'lessons', { forceRefresh: true }).catch(() => []);
        for (const l of lessonsDocs) {
          lessons.push({ id: l.id, grade: gradeName, subject: subjectName, board: d.board || '', topic: l.topic || l.id, ...l });
        }
      } else {
        // Legacy lesson at grade root
        const meta = await getSubDocument(gradeName, d.id, 'content', 'meta').catch(() => null);
        lessons.push({ id: d.id, grade: gradeName, subject: d.subject || meta?.subject || '', topic: d.topic || meta?.topic || d.id, ...(meta || {}) });
      }
    }
  }
  return lessons;
};


// Approve a daily report by a Cluster Head/Center Head/Admin
export const approveDailyReport = async (reportId, approverId) => {
  const docRef = doc(db, COLLECTIONS.DAILY_REPORTS, reportId);
  await updateDoc(docRef, {
    approved: true,
    approvedBy: approverId,
    approvedAt: Timestamp.now(),
    updatedAt: Timestamp.now()
  });
  clearCachePrefix('dailyReports');
  clearCachePrefix('dailyReportByIdentity');
};

approveDailyReport.clearCacheOnWrite = () => clearCachePrefix('dailyReports');

// Reject (unapprove) a daily report by a Cluster Head/Center Head/Admin
export const rejectDailyReport = async (reportId) => {
  const docRef = doc(db, COLLECTIONS.DAILY_REPORTS, reportId);
  await updateDoc(docRef, {
    approved: false,
    approvedBy: null,
    approvedAt: null,
    isPublished: false,
    publishedBy: null,
    publishedAt: null,
    status: 'draft',
    submittedAt: null,
    updatedAt: Timestamp.now()
  });
  clearCachePrefix('dailyReports');
  clearCachePrefix('dailyReportByIdentity');
};

rejectDailyReport.clearCacheOnWrite = () => clearCachePrefix('dailyReports');

// Publish a daily report (set isPublished to true)
export const publishDailyReport = async (reportId, publisherId) => {
  const docRef = doc(db, COLLECTIONS.DAILY_REPORTS, reportId);
  await updateDoc(docRef, {
    isPublished: true,
    publishedBy: publisherId,
    publishedAt: Timestamp.now(),
    updatedAt: Timestamp.now()
  });
  clearCachePrefix('dailyReports');
  clearCachePrefix('dailyReportByIdentity');
};

publishDailyReport.clearCacheOnWrite = () => clearCachePrefix('dailyReports');

// Unpublish a daily report (set isPublished to false)
export const unpublishDailyReport = async (reportId) => {
  const docRef = doc(db, COLLECTIONS.DAILY_REPORTS, reportId);
  await updateDoc(docRef, {
    isPublished: false,
    updatedAt: Timestamp.now()
  });
  clearCachePrefix('dailyReports');
  clearCachePrefix('dailyReportByIdentity');
};

unpublishDailyReport.clearCacheOnWrite = () => clearCachePrefix('dailyReports');

deleteDailyReport.clearCacheOnWrite = () => clearCachePrefix('dailyReports');

// ============ WEEKLY PLANS ============
// WeeklyPlans is stored as a single document with nested structure: { weekKey: { studentId: { sessionType: [sessionIds] } } }
export const getWeeklyPlans = async (opts = {}) => {
  const cacheKey = _makeKey('weeklyPlans', opts);
  if (!opts.forceRefresh) {
    const cached = _getCached(cacheKey);
    if (cached) return cached;
  }
  const querySnapshot = await getDocs(collection(db, COLLECTIONS.WEEKLY_PLANS));
  // If no documents exist return empty
  const docs = querySnapshot.docs;
  if (docs.length === 0) {
    _setCached(cacheKey, {});
    return {};
  }

  // If there are multiple per-week documents or docs with weekStartDate field
  // treat them as individual week docs and return a mapping { weekStartDate: plans }
  if (docs.length > 1 || (docs[0].data() && 'weekStartDate' in docs[0].data())) {
    const map = {};
    docs.forEach(d => {
      const data = d.data() || {};
      const key = data.weekStartDate || d.id;
      map[key] = data.plans || (data.sessions || data || {});
    });
    _setCached(cacheKey, map);
    return map;
  }

  // Single-document legacy shape: return the document's data as-is
  const docData = docs[0].data();
  _setCached(cacheKey, docData);
  return docData;
};

export const addWeeklyPlan = async (planData) => {
  const docRef = await addDoc(collection(db, COLLECTIONS.WEEKLY_PLANS), {
    ...planData,
    createdAt: Timestamp.now(),
    updatedAt: Timestamp.now()
  });
  // weekly plans changed, clear weeklyPlans cache
  clearCachePrefix('weeklyPlans');
  return docRef.id;
};

export const updateWeeklyPlan = async (planData) => {
  // planData may be in one of two shapes:
  // 1) legacy single-container: { weekKey: { ... }, otherWeekKey: { ... } }
  // 2) single-week doc: { weekStartDate: 'yyyy-MM-dd', plans: { ... } }
  // 3) caller passing a per-week mapping (same as legacy container)
  const querySnapshot = await getDocs(collection(db, COLLECTIONS.WEEKLY_PLANS));
  const docs = querySnapshot.docs || [];

  // Helper to write a single week doc (create or replace by id/weekStartDate)
  const _writeWeek = async (weekKey, weekObj) => {
    // prefer explicit doc whose id === weekKey or whose weekStartDate matches
    const matching = docs.find(d => String(d.id) === String(weekKey) || String((d.data() || {}).weekStartDate) === String(weekKey));
    const payload = (weekObj && typeof weekObj === 'object' && ('plans' in weekObj || 'weekStartDate' in weekObj))
      ? { weekStartDate: weekKey, plans: weekObj.plans || weekObj }
      : { weekStartDate: weekKey, plans: weekObj };

    if (matching) {
      // overwrite/merge into existing per-week doc
      const docRef = doc(db, COLLECTIONS.WEEKLY_PLANS, matching.id);
      await setDoc(docRef, { ...payload, updatedAt: Timestamp.now() }, { merge: true });
    } else {
      // create a new document with id = weekKey so reads are deterministic
      const docRef = doc(db, COLLECTIONS.WEEKLY_PLANS, String(weekKey));
      await setDoc(docRef, { ...payload, createdAt: Timestamp.now(), updatedAt: Timestamp.now() });
    }
  };

  // If collection appears to be per-week (many docs or docs contain weekStartDate), write per-week documents
  const perWeekMode = docs.length > 1 || (docs[0] && (docs[0].data() || {}) && 'weekStartDate' in (docs[0].data() || {}));

  if (perWeekMode) {
    // accept either a container-like map or a single-week object
    if (planData && typeof planData === 'object' && Object.keys(planData).every(k => String(k).match(/^\d{4}-\d{2}-\d{2}$/))) {
      // planData is a map of weekKey -> weekDoc
      for (const [wk, wkObj] of Object.entries(planData)) {
        await _writeWeek(wk, wkObj);
      }
    } else if (planData && typeof planData === 'object' && planData.weekStartDate) {
      await _writeWeek(planData.weekStartDate, planData);
    } else {
      // fallback: try to merge planData keys into existing docs where possible
      for (const [k, v] of Object.entries(planData || {})) {
        if (String(k).match(/^\d{4}-\d{2}-\d{2}$/)) {
          await _writeWeek(k, v);
        }
      }
    }

    clearCachePrefix('weeklyPlans');
    return;
  }

  // Single-document legacy shape — update the first (and only) document
  if (docs.length > 0) {
    const docRef = doc(db, COLLECTIONS.WEEKLY_PLANS, docs[0].id);
    await updateDoc(docRef, {
      ...planData,
      updatedAt: Timestamp.now()
    });
    clearCachePrefix('weeklyPlans');
    return;
  }

  // No documents exist — create a new single-container document
  await addDoc(collection(db, COLLECTIONS.WEEKLY_PLANS), {
    ...planData,
    createdAt: Timestamp.now(),
    updatedAt: Timestamp.now()
  });
  clearCachePrefix('weeklyPlans');
};

// ============ TIMETABLE ============
// Timetable is stored as a single document with nested structure: { day: { slot: [sessions] } }
export const getTimetable = async (opts = {}) => {
  const cacheKey = _makeKey('timetable', opts);
  if (!opts.forceRefresh) {
    const cached = _getCached(cacheKey);
    if (cached) return cached;
  }
  const querySnapshot = await getDocs(collection(db, COLLECTIONS.TIMETABLE));
  const docs = querySnapshot.docs;
  if (docs.length === 0) {
    _setCached(cacheKey, {});
    return {};
  }

  // Detect if this collection uses nested slots/assignments subcollections (new schema).
  // We'll check if any doc has a 'slots' subcollection and, if so, build the normalized shape.
  const slotChecks = await Promise.all(docs.map(d => listSubCollectionDocs(COLLECTIONS.TIMETABLE, d.id, 'slots').catch(() => [])));
  const hasSlots = slotChecks.some(arr => Array.isArray(arr) && arr.length > 0);

  if (hasSlots) {
    console.debug('firestoreService.getTimetable: reading nested slots/assignments schema');
    const combined = {};
    for (const d of docs) {
      const data = d.data() || {};
      const dayKey = (data.day || d.id).toString().toLowerCase();
      combined[dayKey] = combined[dayKey] || {};
      const slots = await listSubCollectionDocs(COLLECTIONS.TIMETABLE, d.id, 'slots').catch(() => []);
      for (const slotDoc of slots) {
        const slotId = slotDoc.id;
        const assignments = await listSlotAssignments(d.id, slotId).catch(() => []);
        combined[dayKey][slotId] = assignments;
      }
    }
    _setCached(cacheKey, combined);
    return combined;
  }

  // If a canonical doc exists (set by normalization or previous writes), prefer it for legacy shapes
  const canonicalDoc = docs.find(d => (d.data() || {}).canonical);
  if (canonicalDoc) {
    console.debug('firestoreService.getTimetable: found canonical doc', canonicalDoc.id);
    const docData = canonicalDoc.data() || {};
    _setCached(cacheKey, docData);
    return docData;
  }

  // If multiple timetable docs (e.g., one per weekday) merge them into a single object keyed by day
  if (docs.length > 1) {
    console.debug('firestoreService.getTimetable: merging docs', docs.map(d => d.id));
    const combined = {};
    docs.forEach(d => {
      const data = d.data() || {};
      const dayKey = (data.day || d.id).toString().toLowerCase();
      combined[dayKey] = data.sessions || data;
    });
    _setCached(cacheKey, combined);
    return combined;
  }

  // Single-document legacy shape
  const docData = docs[0].data();
  _setCached(cacheKey, docData);
  return docData;
};

export const updateTimetable = async (timetableData) => {
  console.debug('firestoreService.updateTimetable called', timetableData);
  const querySnapshot = await getDocs(collection(db, COLLECTIONS.TIMETABLE));

  // If nested slots/assignments schema exists, persist changes there so refreshes do not drop updates.
  const docs = querySnapshot.docs;
  const slotChecks = await Promise.all(docs.map(d => listSubCollectionDocs(COLLECTIONS.TIMETABLE, d.id, 'slots').catch(() => [])));
  const hasSlots = slotChecks.some(arr => Array.isArray(arr) && arr.length > 0);
  if (hasSlots) {
    const dayKeys = Object.keys(timetableData || {}).filter(k => !['createdAt', 'updatedAt', 'canonical'].includes(k));
    const sanitizeId = (val) => String(val || '').replace(/\//g, '-');
    let primaryDocId = docs[0]?.id || null;

    for (const dayKeyRaw of dayKeys) {
      const dayKey = String(dayKeyRaw).toLowerCase();
      if (!primaryDocId) primaryDocId = dayKey;
      const dayDocRef = doc(db, COLLECTIONS.TIMETABLE, dayKey);
      await setDoc(dayDocRef, { day: dayKey, updatedAt: Timestamp.now(), canonical: true }, { merge: true });

      const daySlots = timetableData[dayKeyRaw] || {};
      for (const slotIdRaw of Object.keys(daySlots)) {
        const slotId = String(slotIdRaw);
        await setSubDocument(COLLECTIONS.TIMETABLE, dayKey, 'slots', slotId, { slot: slotId, updatedAt: Timestamp.now() });

        // Remove existing assignments for this slot, then recreate from the provided data
        const existing = await listNestedSubCollectionDocs(COLLECTIONS.TIMETABLE, dayKey, 'slots', slotId, 'assignments').catch(() => []);
        for (const a of existing) {
          await deleteNestedSubDocument(COLLECTIONS.TIMETABLE, dayKey, 'slots', slotId, 'assignments', a.id);
        }

        const assignments = Array.isArray(daySlots[slotIdRaw]) ? daySlots[slotIdRaw] : [];
        for (let index = 0; index < assignments.length; index += 1) {
          const assignment = assignments[index];
          const mentorId = assignment?.mentorId || assignment?.mentorRef || assignment?.mentor || '';
          const studentIds = Array.isArray(assignment?.studentIds)
            ? assignment.studentIds
            : (Array.isArray(assignment?.studentRefs) ? assignment.studentRefs : (assignment?.studentIds ? [assignment.studentIds] : []));
          const sessionType = assignment?.sessionType || assignment?.subject || '';
          const subject = assignment?.subject || assignment?.sessionType || '';
          const idBase = `${slotId}__${mentorId}__${sessionType}__${(studentIds || []).slice().sort().join('-')}__${index}`;
          const assignmentId = sanitizeId(assignment?.id || idBase || `assign_${Date.now()}_${index}`);
          await setSlotAssignment(dayKey, slotId, assignmentId, { mentorId, studentIds, sessionType, subject });
        }
      }
    }

    clearCachePrefix('timetable');
    console.debug('firestoreService.updateTimetable updated nested timetable schema', { dayKeys });
    return primaryDocId;
  }

  // If a canonical doc exists, always prefer using it
  const canonicalDoc = querySnapshot.docs.find(d => (d.data() || {}).canonical);
  if (canonicalDoc) {
    const docRef = doc(db, COLLECTIONS.TIMETABLE, canonicalDoc.id);
    await setDoc(docRef, {
      ...timetableData,
      updatedAt: Timestamp.now(),
      canonical: true
    });
    clearCachePrefix('timetable');
    console.debug('firestoreService.updateTimetable updated canonical doc', docRef.id);
    return docRef.id;
  }

  if (querySnapshot.docs.length > 0) {
    // Prefer a document that already contains overlapping day/slot keys
    let chosenDoc = null;
    for (const d of querySnapshot.docs) {
      const data = d.data() || {};
      const keys = Object.keys(data || {});
      const overlap = keys.filter(k => k in timetableData || (typeof timetableData === 'object' && k === 'day' && k in timetableData)).length;
      if (overlap > 0) {
        chosenDoc = d;
        break;
      }
    }
    const docRef = doc(db, COLLECTIONS.TIMETABLE, (chosenDoc || querySnapshot.docs[0]).id);
    // Overwrite the document with the canonical timetable shape to avoid partial merge inconsistencies
    await setDoc(docRef, {
      ...timetableData,
      updatedAt: Timestamp.now(),
      canonical: true
    });
    clearCachePrefix('timetable');
    console.debug('firestoreService.updateTimetable updated doc', docRef.id);
    return docRef.id;
  } else {
    // Create new document if none exists — make this the canonical doc
    const docRef = await addDoc(collection(db, COLLECTIONS.TIMETABLE), {
      ...timetableData,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
      canonical: true
    });
    clearCachePrefix('timetable');
    console.debug('firestoreService.updateTimetable created doc', docRef.id);
    return docRef.id;
  }
};

// Return info about timetable documents (ids, primary doc if marked)
export const getTimetableInfo = async () => {
  const querySnapshot = await getDocs(collection(db, COLLECTIONS.TIMETABLE));
  const docs = querySnapshot.docs;
  if (docs.length === 0) return { exists: false, docs: [] };
  const canonicalDoc = docs.find(d => (d.data() || {}).canonical);
  return {
    exists: true,
    primaryDocId: canonicalDoc ? canonicalDoc.id : docs[0].id,
    docs: docs.map(d => d.id)
  };
};

// Normalize/merge multiple timetable documents into a single canonical document
export const normalizeTimetableDocs = async () => {
  const querySnapshot = await getDocs(collection(db, COLLECTIONS.TIMETABLE));
  const docs = querySnapshot.docs;
  if (docs.length === 0) return { merged: false, message: 'No timetable documents found' };

  // Merge by day -> sessionKey -> array of assignments
  const merged = {};
  const seenAssignments = new Set();

  for (const d of docs) {
    const data = d.data() || {};
    Object.keys(data).forEach(dayKey => {
      if (dayKey === 'createdAt' || dayKey === 'updatedAt') return;
      const dayVal = data[dayKey] || {};
      if (typeof dayVal !== 'object') return;
      merged[dayKey] = merged[dayKey] || {};
      Object.keys(dayVal).forEach(sessionKey => {
        const arr = Array.isArray(dayVal[sessionKey]) ? dayVal[sessionKey] : [];
        merged[dayKey][sessionKey] = merged[dayKey][sessionKey] || [];
        arr.forEach(a => {
          // create a stable fingerprint
          const fingerprint = `${sessionKey}::${String(a.mentorId||'')}/${String(a.sessionType||'')}/${JSON.stringify((a.studentIds||[]).slice().sort())}`;
          if (!seenAssignments.has(fingerprint)) {
            seenAssignments.add(fingerprint);
            merged[dayKey][sessionKey].push(a);
          }
        });
      });
    });
  }

  // Write merged object to the first doc (overwrite) and mark it as canonical
  const primaryDoc = docs[0];
  const primaryRef = doc(db, COLLECTIONS.TIMETABLE, primaryDoc.id);
  await setDoc(primaryRef, {
    ...merged,
    updatedAt: Timestamp.now(),
    canonical: true
  });
  clearCachePrefix('timetable');
  console.debug('normalizeTimetableDocs: wrote canonical doc', primaryDoc.id);

  // Delete other docs (move to backup if needed). For safety, keep only docs that have id !== primary
  const deleted = [];
  for (const d of docs.slice(1)) {
    try {
      await deleteDoc(doc(db, COLLECTIONS.TIMETABLE, d.id));
      deleted.push(d.id);
    } catch (err) {
      console.warn('normalizeTimetableDocs: failed to delete doc', d.id, err);
    }
  }

  return { merged: true, primaryDocId: primaryDoc.id, deleted };
};

// Inspect timetable collection and nested schema; returns structured JSON useful for admins
export const inspectTimetableDocs = async () => {
  const querySnapshot = await getDocs(collection(db, COLLECTIONS.TIMETABLE));
  const docs = querySnapshot.docs;
  const result = [];
  let totalSlots = 0;
  let totalAssignments = 0;

  for (const d of docs) {
    const data = d.data() || {};
    const docId = d.id;
    const docObj = { docId, keys: Object.keys(data), day: data.day || docId, slots: [] };

    // list slots
    const slots = await listSubCollectionDocs(COLLECTIONS.TIMETABLE, docId, 'slots').catch(() => []);
    totalSlots += slots.length;
    for (const s of slots) {
      const slotId = s.id;
      const slotData = s || {};
      const assigns = await listNestedSubCollectionDocs(COLLECTIONS.TIMETABLE, docId, 'slots', slotId, 'assignments').catch(() => []);
      totalAssignments += assigns.length;
      const normalizedAssigns = assigns.map(a => ({ id: a.id, mentorRef: a.mentorRef?.id || a.mentorId || null, studentRefs: Array.isArray(a.studentRefs) ? a.studentRefs.map(r => r.id || r) : null, studentIds: a.studentIds || null, sessionType: a.sessionType || null, subject: a.subject || null, rawKeys: Object.keys(a) }));
      docObj.slots.push({ slotId, slotData, assignments: normalizedAssigns });
    }

    result.push(docObj);
  }

  return { docs: result, summary: { totalDocs: docs.length, totalSlots, totalAssignments } };
};

export const addTimetableEntry = async (entryData) => {
  const docRef = await addDoc(collection(db, COLLECTIONS.TIMETABLE), {
    ...entryData,
    createdAt: Timestamp.now(),
    updatedAt: Timestamp.now()
  });
  clearCachePrefix('timetable');
  return docRef.id;
};

// ============ SESSIONS ============
// Sessions is stored as a single document with nested structure: { academic: [], body: [], mind: [], cbcs: [], lifeSkills: [] }
export const getSessions = async (opts = {}) => {
  const cacheKey = _makeKey('sessions', opts);
  if (!opts.forceRefresh) {
    const cached = _getCached(cacheKey);
    if (cached) return cached;
  }
  const querySnapshot = await getDocs(collection(db, COLLECTIONS.SESSIONS));
  const docs = querySnapshot.docs;
  if (docs.length === 0) {
    return { academic: [], body: [], mind: [], cbcs: [], lifeSkills: [] };
  }

  // Two supported schemas:
  // 1) Many documents, each representing a session (screenshot)
  // 2) Single document containing arrays or objects of sessions (legacy)

  const looksLikePerDocument = docs.length > 1 || (docs[0].data() && ('category' in docs[0].data() || 'subject' in docs[0].data()));

  const normalizeItem = (item, fallbackId) => {
    const name = item?.name || item?.title || item?.sessionName || '';
    const subjectRaw = item?.subject || item?.type || item?.category || '';
    const subject = typeof subjectRaw === 'string' ? subjectRaw.toLowerCase() : '';
    const grade = item?.grade ?? item?.class ?? item?.level;
    const idCandidate = item?.id || item?.key || item?.code || fallbackId || (name ? name : `${subject}-${grade ?? ''}`);
    return {
      ...item,
      id: idCandidate,
      name: name || `${(subject || '').toUpperCase()} ${grade ?? ''}`.trim(),
      subject,
      grade
    };
  };

  if (looksLikePerDocument) {
    const result = { academic: [], body: [], mind: [], cbcs: [], lifeSkills: [], skills: [] };
    docs.forEach(d => {
      const data = d.data() || {};
      const categoryRaw = data.category || data.type || data.sessionType || '';
      const category = typeof categoryRaw === 'string' ? categoryRaw.toLowerCase() : '';
      const item = normalizeItem(data, d.id);

      switch (category) {
        case 'academic':
          // Ensure subject exists for academic: english/math/science
          item.subject = (item.subject || '').toLowerCase();
          result.academic.push(item);
          break;
        case 'body':
          result.body.push(item);
          break;
        case 'mind':
          result.mind.push(item);
          break;
        case 'skills':
        case 'skill':
          result.skills.push(item);
          break;
        case 'cbcs':
          result.cbcs.push({ ...item, sessionType: 'cbcs' });
          break;
        case 'life_skills':
        case 'lifeskills':
        case 'lifeSkills':
          result.lifeSkills.push(item);
          break;
        default:
          // If subject hints it's academic, bucket it as academic
          if (['english', 'math', 'science'].includes((item.subject || '').toLowerCase())) {
            result.academic.push(item);
          }
          break;
      }
    });
    _setCached(cacheKey, result);
    return result;
  }

  // Legacy single-document shape
  const docData = docs[0].data();
  const base = docData?.sessions && typeof docData.sessions === 'object' ? docData.sessions : docData || {};

  const pick = (...keys) => keys.find(k => base && Object.prototype.hasOwnProperty.call(base, k));
  const academicKey = pick('academic', 'academics', 'intellect');
  const bodyKey = pick('body', 'physical');
  const mindKey = pick('mind', 'mental');
  const cbcsKey = pick('cbcs', 'CBCS');
  const lifeSkillsKey = pick('lifeSkills', 'life_skills', 'lifeSkill', 'lifeskills');
  const skillsKey = pick('skills', 'skill', 'skillActivities', 'skillsActivities');
  const ensureArray = (val) => Array.isArray(val) ? val : [];

  // Academic can be an array or an object keyed by grade
  let academicRaw = base?.[academicKey];
  let academicArr = [];
  if (Array.isArray(academicRaw)) {
    academicArr = academicRaw.map(i => normalizeItem(i));
  } else if (academicRaw && typeof academicRaw === 'object') {
    Object.entries(academicRaw).forEach(([gradeKey, items]) => {
      if (Array.isArray(items)) {
        items.forEach(i => academicArr.push(normalizeItem({ ...i, grade: i?.grade ?? gradeKey })));
      }
    });
  }

  const legacyResult = {
    academic: academicArr,
    body: ensureArray(base?.[bodyKey]).map(i => normalizeItem(i)),
    mind: ensureArray(base?.[mindKey]).map(i => normalizeItem(i)),
    skills: ensureArray(base?.[skillsKey]).map(i => normalizeItem(i)),
    cbcs: ensureArray(base?.[cbcsKey]).map(i => normalizeItem({ ...i, sessionType: 'cbcs' })),
    lifeSkills: ensureArray(base?.[lifeSkillsKey]).map(i => normalizeItem(i))
  };
  _setCached(cacheKey, legacyResult);
  return legacyResult;
};

export const updateSessions = async (sessionsData) => {
  const querySnapshot = await getDocs(collection(db, COLLECTIONS.SESSIONS));
  if (querySnapshot.docs.length > 0) {
    // Update the first document
    const docRef = doc(db, COLLECTIONS.SESSIONS, querySnapshot.docs[0].id);
    await updateDoc(docRef, {
      ...sessionsData,
      updatedAt: Timestamp.now()
    });
    clearCachePrefix('sessions');
  } else {
    // Create new document if none exists
    await addDoc(collection(db, COLLECTIONS.SESSIONS), {
      ...sessionsData,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    });
    clearCachePrefix('sessions');
  }
};

// ============ IMPORTANT DATES ============
export const getImportantDates = async (opts = {}) => {
  const cacheKey = _makeKey('importantDates', opts);
  if (!opts.forceRefresh) {
    const cached = _getCached(cacheKey);
    if (cached) return cached;
  }
  const q = query(collection(db, COLLECTIONS.IMPORTANT_DATES), orderBy('date', 'asc'));
  const querySnapshot = await getDocs(q);
  const result = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  _setCached(cacheKey, result);
  return result;
};

export const addImportantDate = async (dateData) => {
  const docRef = await addDoc(collection(db, COLLECTIONS.IMPORTANT_DATES), {
    ...dateData,
    createdAt: Timestamp.now(),
    updatedAt: Timestamp.now()
  });
  clearCachePrefix('importantDates');
  return docRef.id;
};

export const updateImportantDate = async (dateId, dateData) => {
  const docRef = doc(db, COLLECTIONS.IMPORTANT_DATES, dateId);
  await updateDoc(docRef, {
    ...dateData,
    updatedAt: Timestamp.now()
  });
  clearCachePrefix('importantDates');
};

export const deleteImportantDate = async (dateId) => {
  await deleteDoc(doc(db, COLLECTIONS.IMPORTANT_DATES, dateId));
  clearCachePrefix('importantDates');
};

// ============ GALLERY ============
export const getGalleryImages = async (opts = {}) => {
  const cacheKey = _makeKey('galleryImages', opts);
  if (!opts.forceRefresh) {
    const cached = _getCached(cacheKey);
    if (cached) return cached;
  }
  const querySnapshot = await getDocs(collection(db, COLLECTIONS.GALLERY));
  const result = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  _setCached(cacheKey, result);
  return result;
};

export const getGalleryPhotos = getGalleryImages; // Alias for consistency

export const addGalleryImage = async (imageData) => {
  const docRef = await addDoc(collection(db, COLLECTIONS.GALLERY), {
    ...imageData,
    createdAt: Timestamp.now()
  });
  clearCachePrefix('galleryImages');
  return docRef.id;
};

export const addGalleryPhoto = addGalleryImage; // Alias for consistency

export const updateGalleryPhoto = async (photoId, photoData) => {
  const docRef = doc(db, COLLECTIONS.GALLERY, photoId);
  await updateDoc(docRef, {
    ...photoData,
    updatedAt: Timestamp.now()
  });
  clearCachePrefix('galleryImages');
};

export const deleteGalleryImage = async (imageId) => {
  await deleteDoc(doc(db, COLLECTIONS.GALLERY, imageId));
  clearCachePrefix('galleryImages');
};

export const deleteGalleryPhoto = deleteGalleryImage; // Alias for consistency

// ============ BROADCASTS ============
export const getBroadcasts = async (opts = {}) => {
  const cacheKey = _makeKey('broadcasts', opts);
  if (!opts.forceRefresh) {
    const cached = _getCached(cacheKey);
    if (cached) return cached;
  }
  const q = query(collection(db, COLLECTIONS.BROADCASTS), orderBy('createdAt', 'desc'));
  const querySnapshot = await getDocs(q);
  const result = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  _setCached(cacheKey, result);
  return result;
};

export const addBroadcast = async (broadcastData) => {
  const docRef = await addDoc(collection(db, COLLECTIONS.BROADCASTS), {
    ...broadcastData,
    createdAt: Timestamp.now()
  });
  clearCachePrefix('broadcasts');
  return docRef.id;
};

// ============ SETTINGS ============
export const getSettings = async (opts = {}) => {
  const cacheKey = _makeKey('settings', opts);
  if (!opts.forceRefresh) {
    const cached = _getCached(cacheKey);
    if (cached) return cached;
  }
  const querySnapshot = await getDocs(collection(db, COLLECTIONS.SETTINGS));
  const val = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))[0] || null;
  const normalized = val
    ? {
        ...val,
        bodyActivities: Array.isArray(val.bodyActivities) ? val.bodyActivities : [],
        bodyActivityAttributes: val.bodyActivityAttributes || {},
        mindActivities: Array.isArray(val.mindActivities) ? val.mindActivities : [],
        skillsActivities: Array.isArray(val.skillsActivities) ? val.skillsActivities : [],
        blockedReportWords: Array.isArray(val.blockedReportWords) ? val.blockedReportWords : [],
        skillsAgeGroups: Array.isArray(val.skillsAgeGroups) ? val.skillsAgeGroups : [],
        commonCurriculumFiles: Array.isArray(val.commonCurriculumFiles) ? val.commonCurriculumFiles : [],
        themePreferences: normalizeThemePreferences(val.themePreferences),
        carryForwardUnheldSessionsEnabled: val.carryForwardUnheldSessionsEnabled !== false,
        carryForwardLookbackWeeks: Number(val.carryForwardLookbackWeeks) > 0 ? Number(val.carryForwardLookbackWeeks) : 1
      }
    : {
        carryForwardUnheldSessionsEnabled: true,
        carryForwardLookbackWeeks: 1,
        bodyActivities: [],
        bodyActivityAttributes: {},
        mindActivities: [],
        skillsActivities: [],
        blockedReportWords: [],
        skillsAgeGroups: [],
        commonCurriculumFiles: [],
        themePreferences: normalizeThemePreferences()
      };
  _setCached(cacheKey, normalized);
  return normalized;
};

// ============ ACADEMIC PROGRESS (Manual checks/logs + approvals) ============
/**
 * Schema (per doc)
 * {
 *   studentId,
 *   date, // yyyy-MM-dd for daily checks
 *   notebookStatus: 'Checked'|'Pending'|'Issue',
 *   blueprintStatus: 'Prepared'|'Pending'|'On Track',
 *   chapterProgressText: 'Chapter 5, Section B', // optional cached snapshot
 *   chapterApproved: boolean,
 *   approverId: uid,
 *   approvedAt: Timestamp,
 *   lastManualUpdateBy: uid,
 *   lastManualUpdateAt: Timestamp
 * }
 */
export const getAcademicProgress = async (filters = {}) => {
  const cacheKey = _makeKey('academicProgress', filters);
  if (!filters.forceRefresh) {
    const cached = _getCached(cacheKey);
    if (cached) return cached;
  }

  let q = collection(db, COLLECTIONS.ACADEMIC_PROGRESS);

  if (filters.studentId) {
    q = query(q, where('studentId', '==', filters.studentId));
  }
  if (filters.date) {
    q = query(q, where('date', '==', filters.date));
  }

  const querySnapshot = await getDocs(q);
  const result = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  _setCached(cacheKey, result);
  return result;
};

export const upsertAcademicProgress = async ({ studentId, date, payload }) => {
  // try find existing record for student/date
  const q = query(collection(db, COLLECTIONS.ACADEMIC_PROGRESS), where('studentId', '==', studentId), where('date', '==', date));
  const querySnapshot = await getDocs(q);

  if (querySnapshot.docs.length > 0) {
    const docRef = doc(db, COLLECTIONS.ACADEMIC_PROGRESS, querySnapshot.docs[0].id);
    await updateDoc(docRef, { ...payload, lastManualUpdateAt: Timestamp.now() });
    clearCachePrefix('academicProgress');
    return querySnapshot.docs[0].id;
  }

  const docRef = await addDoc(collection(db, COLLECTIONS.ACADEMIC_PROGRESS), {
    studentId,
    date,
    ...payload,
    createdAt: Timestamp.now(),
    lastManualUpdateAt: Timestamp.now()
  });
  clearCachePrefix('academicProgress');
  return docRef.id;
};

export const approveAcademicProgress = async (recordId, approverId) => {
  const docRef = doc(db, COLLECTIONS.ACADEMIC_PROGRESS, recordId);
  await updateDoc(docRef, {
    chapterApproved: true,
    approverId,
    approvedAt: Timestamp.now(),
    lastManualUpdateAt: Timestamp.now()
  });
  clearCachePrefix('academicProgress');
};

export const updateSettings = async (settingsId, settingsData) => {
  const docRef = doc(db, COLLECTIONS.SETTINGS, settingsId);
  await setDoc(docRef, {
    ...settingsData,
    updatedAt: Timestamp.now()
  }, { merge: true });
  clearCachePrefix('settings');
};

// ============ BATCH OPERATIONS ============
export const batchUpdateReports = async (updates) => {
  const batch = writeBatch(db);
  
  updates.forEach(({ id, data }) => {
    const docRef = doc(db, COLLECTIONS.DAILY_REPORTS, id);
    batch.update(docRef, { ...data, updatedAt: Timestamp.now() });
  });
  
  await batch.commit();
  // Batch updates modified daily reports - invalidate cache
  clearCachePrefix('dailyReports');
};

// ============ WPM REPORTS ============

/**
 * Fetch daily reports for a student that fall within [startDateStr, endDateStr] (inclusive,
 * format "YYYY-MM-DD") and have approved === true AND isPublished === true.
 * Uses client-side date-range filter because Firestore does not have a compound index
 * covering studentId + date-range + approved + isPublished.
 */
export const getDailyReportsForTermAggregation = async (studentId, startDateStr, endDateStr) => {
  const cacheKey = _makeKey('wpmTermAggregation', { studentId, startDateStr, endDateStr });
  const cached = _getCached(cacheKey);
  if (cached) return cached;

  const q = query(
    collection(db, COLLECTIONS.DAILY_REPORTS),
    where('studentId', '==', studentId)
  );
  const snapshot = await getDocs(q);
  const all = snapshot.docs.map((docEntry) => {
    const data = normalizeDailyReport(docEntry.data());
    return {
      ...data,
      id: data?.id || docEntry.id,
      docId: docEntry.id
    };
  });

  const result = all.filter((r) => {
    if (!r.approved || !r.isPublished) return false;
    const d = r.date || '';
    return d >= startDateStr && d <= endDateStr;
  });

  _setCached(cacheKey, result);
  return result;
};

/**
 * Get a single WPM report by Firestore document ID.
 */
export const getWpmReport = async (wpmId) => {
  const docRef = doc(db, COLLECTIONS.WPM_REPORTS, wpmId);
  const snap = await getDoc(docRef);
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
};

/**
 * Get all WPM reports matching optional filters: studentId, mentorId, academicYear, term.
 */
export const getWpmReports = async (filters = {}) => {
  const cacheKey = _makeKey('wpmReports', filters);
  if (!filters.forceRefresh) {
    const cached = _getCached(cacheKey);
    if (cached) return cached;
  }

  let q = collection(db, COLLECTIONS.WPM_REPORTS);
  if (filters.studentId) q = query(q, where('studentId', '==', filters.studentId));
  if (filters.mentorId)  q = query(q, where('mentorId',  '==', filters.mentorId));
  if (filters.academicYear) q = query(q, where('academicYear', '==', filters.academicYear));
  if (filters.term)      q = query(q, where('term', '==', filters.term));

  const snap = await getDocs(q);
  const result = snap.docs.map(d => ({ id: d.id, ...d.data() }));
  _setCached(cacheKey, result);
  return result;
};

/**
 * Look up a WPM report by its identity key: studentId + mentorId + academicYear + term.
 * Returns the first match (or null).
 */
export const getWpmReportByIdentity = async ({ studentId, mentorId, academicYear, term }) => {
  const cacheKey = _makeKey('wpmReportByIdentity', { studentId, mentorId, academicYear, term });
  const cached = _getCached(cacheKey);
  if (cached !== undefined) return cached;

  const q = query(
    collection(db, COLLECTIONS.WPM_REPORTS),
    where('studentId',    '==', studentId),
    where('mentorId',     '==', mentorId),
    where('academicYear', '==', academicYear),
    where('term',         '==', term)
  );
  const snap = await getDocs(q);
  const result = snap.docs.length > 0 ? { id: snap.docs[0].id, ...snap.docs[0].data() } : null;
  _setCached(cacheKey, result);
  return result;
};

/**
 * Create a new WPM report document.
 */
export const addWpmReport = async (data) => {
  const docRef = await addDoc(collection(db, COLLECTIONS.WPM_REPORTS), {
    ...data,
    status: data.status || 'draft',
    approved: false,
    isPublished: false,
    createdAt: Timestamp.now(),
    updatedAt: Timestamp.now(),
  });
  clearCachePrefix('wpmReports');
  clearCachePrefix('wpmReportByIdentity');
  return docRef.id;
};

/**
 * Update an existing WPM report document (partial update).
 */
export const updateWpmReport = async (wpmId, data) => {
  const docRef = doc(db, COLLECTIONS.WPM_REPORTS, wpmId);
  await updateDoc(docRef, { ...data, updatedAt: Timestamp.now() });
  clearCachePrefix('wpmReports');
  clearCachePrefix('wpmReportByIdentity');
};

/**
 * Approve a WPM report (cluster head / center head / admin).
 */
export const approveWpmReport = async (wpmId, approverId) => {
  const docRef = doc(db, COLLECTIONS.WPM_REPORTS, wpmId);
  await updateDoc(docRef, {
    approved: true,
    approvedBy: approverId,
    approvedAt: Timestamp.now(),
    updatedAt: Timestamp.now(),
  });
  clearCachePrefix('wpmReports');
  clearCachePrefix('wpmReportByIdentity');
};

/**
 * Reject (unapprove) a WPM report (cluster head / center head / admin).
 */
export const rejectWpmReport = async (wpmId) => {
  const docRef = doc(db, COLLECTIONS.WPM_REPORTS, wpmId);
  await updateDoc(docRef, {
    approved: false,
    approvedBy: null,
    approvedAt: null,
    isPublished: false,
    publishedBy: null,
    publishedAt: null,
    status: 'draft',
    submittedAt: null,
    updatedAt: Timestamp.now(),
  });
  clearCachePrefix('wpmReports');
  clearCachePrefix('wpmReportByIdentity');
};

/**
 * Publish a WPM report AND project a denormalized snapshot to studentProgressHistory.
 * Only call this after the report is approved.
 */
export const publishWpmReport = async (wpmId, publisherId) => {
  const wpmDoc = await getWpmReport(wpmId);
  if (!wpmDoc) throw new Error('WPM report not found');

  const now = Timestamp.now();

  // 1. Mark the WPM as published.
  const wpmRef = doc(db, COLLECTIONS.WPM_REPORTS, wpmId);
  await updateDoc(wpmRef, {
    isPublished: true,
    publishedBy: publisherId,
    publishedAt: now,
    updatedAt: now,
  });

  // 2. Build the denormalized progress-history entry.
  const historyPayload = {
    studentId:   wpmDoc.studentId,
    academicYear: wpmDoc.academicYear,
    term:        wpmDoc.term,
    termLabel:   wpmDoc.termLabel || wpmDoc.term,
    publishedAt: now,
    publishedBy: publisherId,
    wpmReportId: wpmId,

    physicalFitnessSnapshot:      wpmDoc.physicalFitnessSnapshot      || {},
    physicalFitnessSnapshotNotes: wpmDoc.physicalFitnessSnapshot?.notes || '',
    emotionalWellbeingSnapshot:      wpmDoc.emotionalWellbeingSnapshot      || {},
    emotionalWellbeingSnapshotNotes: wpmDoc.emotionalWellbeingSnapshot?.notes || '',

    academicSkills:      wpmDoc.academicSkills      || {},
    academicSkillsNotes: wpmDoc.academicSkills?.notes || '',
    learningSkills:      wpmDoc.learningSkills      || {},
    learningSkillsNotes: wpmDoc.learningSkills?.notes || '',
    speechLanguageSkills:      wpmDoc.speechLanguageSkills      || {},
    speechLanguageSkillsNotes: wpmDoc.speechLanguageSkills?.notes || '',

    bodyActivitiesSummary: Object.entries(wpmDoc.body?.activities || {}).map(([k, a]) => ({
      activityKey:      k,
      activityName:     a.activityName     || k,
      observationNotes: a.observationNotes || '',
      developmentPlan:  a.developmentPlan  || '',
      scores:           a.scores           || {},
    })),

    intellectCoreSubjectsSummary: Object.entries(wpmDoc.intellectCoreSubjects || {}).map(([k, s]) => ({
      subjectKey:        k,
      subjectName:       s.subjectName       || k,
      lessonsDone:       s.lessonsDone        || '',
      percentAccuracy:   s.percentAccuracy    || '',
      comprehensionLevel: s.comprehensionLevel || '',
      observationNotes:  s.observationNotes   || '',
      developmentPlan:   s.developmentPlan    || '',
    })),

    closingNoteByMentor: wpmDoc.closingNoteByMentor || '',
    sourceMeta:          wpmDoc.sourceMeta           || {},
    updatedAt: now,
  };

  // 3. Upsert studentProgressHistory (one entry per student+year+term).
  const hq = query(
    collection(db, COLLECTIONS.STUDENT_PROGRESS_HISTORY),
    where('studentId',    '==', wpmDoc.studentId),
    where('academicYear', '==', wpmDoc.academicYear),
    where('term',         '==', wpmDoc.term)
  );
  const hSnap = await getDocs(hq);
  if (hSnap.docs.length > 0) {
    const existRef = doc(db, COLLECTIONS.STUDENT_PROGRESS_HISTORY, hSnap.docs[0].id);
    await updateDoc(existRef, historyPayload);
  } else {
    await addDoc(collection(db, COLLECTIONS.STUDENT_PROGRESS_HISTORY), {
      ...historyPayload,
      createdAt: now,
    });
  }

  clearCachePrefix('wpmReports');
  clearCachePrefix('wpmReportByIdentity');
  clearCachePrefix('studentProgressHistory');
};

/**
 * Un-publish a WPM report. Does NOT remove the studentProgressHistory entry.
 */
export const unpublishWpmReport = async (wpmId) => {
  const docRef = doc(db, COLLECTIONS.WPM_REPORTS, wpmId);
  await updateDoc(docRef, {
    isPublished:  false,
    publishedBy:  null,
    publishedAt:  null,
    updatedAt:    Timestamp.now(),
  });
  clearCachePrefix('wpmReports');
  clearCachePrefix('wpmReportByIdentity');
};

// ============ STUDENT PROGRESS HISTORY ============

/**
 * Fetch all published WPM timeline entries for a student.
 */
export const getStudentProgressHistory = async (studentId, opts = {}) => {
  const cacheKey = _makeKey('studentProgressHistory', { studentId, ...opts });
  if (!opts.forceRefresh) {
    const cached = _getCached(cacheKey);
    if (cached) return cached;
  }

  const q = query(
    collection(db, COLLECTIONS.STUDENT_PROGRESS_HISTORY),
    where('studentId', '==', studentId)
  );
  const snap = await getDocs(q);
  const result = snap.docs.map(d => ({ id: d.id, ...d.data() }));
  _setCached(cacheKey, result);
  return result;
};

// ============ WPM MILESTONE CONFIG ============

/**
 * Fetch the WPM milestone config doc from Firestore.
 * Returns null if the doc does not exist (not yet seeded).
 */
export const getWpmMilestoneConfig = async (opts = {}) => {
  const cacheKey = _makeKey('wpmMilestoneConfig', {});
  if (!opts.forceRefresh) {
    const cached = _getCached(cacheKey);
    if (cached !== undefined) return cached;
  }
  const docRef = doc(db, COLLECTIONS.WPM_MILESTONE_CONFIG, 'config');
  const snap = await getDoc(docRef);
  const result = snap.exists() ? { id: snap.id, ...snap.data() } : null;
  _setCached(cacheKey, result);
  return result;
};

/**
 * Seed the initial WPM milestone config. Overwrites any existing doc.
 * Call from the Data Seeding page only.
 */
export const seedWpmMilestoneConfig = async (configData) => {
  const docRef = doc(db, COLLECTIONS.WPM_MILESTONE_CONFIG, 'config');
  await setDoc(docRef, {
    ...configData,
    updatedAt: Timestamp.now(),
    createdAt: Timestamp.now(),
  });
  clearCachePrefix('wpmMilestoneConfig');
};

/**
 * Update a single section of the WPM milestone config.
 * Called from App Settings when the user saves a section.
 *
 * @param {string} sectionId  - e.g. 'physicalFitnessSnapshot'
 * @param {Object} sectionData - the updated section object (attributes + levelOrder + levelLabels + levelType + label)
 */
export const updateWpmMilestoneConfigSection = async (sectionId, sectionData) => {
  const docRef = doc(db, COLLECTIONS.WPM_MILESTONE_CONFIG, 'config');
  await setDoc(
    docRef,
    {
      [sectionId]: sectionData,
      updatedAt: Timestamp.now(),
    },
    { merge: true },
  );
  clearCachePrefix('wpmMilestoneConfig');
};

/** Save the complete WPM configuration in one write. This keeps attribute moves
 * between sections consistent for every screen that reads the configuration. */
export const saveWpmMilestoneConfig = async (configData) => {
  const docRef = doc(db, COLLECTIONS.WPM_MILESTONE_CONFIG, 'config');
  // Firestore does not accept undefined. Strip it recursively so optional
  // compatibility metadata cannot block a settings save.
  const withoutUndefined = (value) => {
    if (Array.isArray(value)) return value.map(withoutUndefined);
    if (value && typeof value === 'object' && !(value instanceof Date) && !value.toDate) {
      return Object.fromEntries(Object.entries(value)
        .filter(([, entry]) => entry !== undefined)
        .map(([key, entry]) => [key, withoutUndefined(entry)]));
    }
    return value;
  };
  await setDoc(docRef, { ...withoutUndefined(configData), updatedAt: Timestamp.now() });
  clearCachePrefix('wpmMilestoneConfig');
};

/** Receive config changes immediately (including changes made in another tab). */
export const subscribeWpmMilestoneConfig = (callback, onError) => {
  const docRef = doc(db, COLLECTIONS.WPM_MILESTONE_CONFIG, 'config');
  return onSnapshot(docRef, (snap) => {
    const result = snap.exists() ? { id: snap.id, ...snap.data() } : null;
    _setCached(_makeKey('wpmMilestoneConfig', {}), result);
    callback(result);
  }, onError);
};

export const getExternalAssessments = async (opts = {}) => {
  return listCollectionDocs(COLLECTIONS.EXTERNAL_ASSESSMENTS, opts);
};

export const addExternalAssessment = async (data) => {
  clearCachePrefix(`collection:${COLLECTIONS.EXTERNAL_ASSESSMENTS}`);
  const docRef = await addDoc(collection(db, COLLECTIONS.EXTERNAL_ASSESSMENTS), {
    ...data,
    createdAt: Timestamp.now()
  });
  return docRef.id;
};

export const getStudentIntellectProgress = async (studentId) => {
  const docRef = doc(db, COLLECTIONS.STUDENT_INTELLECT_PROGRESS, studentId);
  const snap = await getDoc(docRef);
  return snap.exists() ? { id: snap.id, ...snap.data() } : { id: studentId, topicsStatus: {}, chapterProofs: {}, adminOverrides: {} };
};

export const saveStudentIntellectProgress = async (studentId, data) => {
  const docRef = doc(db, COLLECTIONS.STUDENT_INTELLECT_PROGRESS, studentId);
  await setDoc(docRef, data, { merge: true });
};

export const getStudentBaselineObservation = async (studentId) => {
  const docRef = doc(db, COLLECTIONS.STUDENT_BASELINE_OBSERVATIONS, studentId);
  const snap = await getDoc(docRef);
  return snap.exists() ? { id: snap.id, ...snap.data() } : { id: studentId, checkboxStates: {}, developmentPlans: {} };
};

export const saveStudentBaselineObservation = async (studentId, data) => {
  const docRef = doc(db, COLLECTIONS.STUDENT_BASELINE_OBSERVATIONS, studentId);
  await setDoc(docRef, data, { merge: true });
};

export { COLLECTIONS };
